import React, { useState } from 'react';
import {
  Box,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Text,
  TextArea,
  Button,
  useToast,
} from '@razorpay/blade/components';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import employeeBankAccountApis, {
  BankAccountRequest,
  GET_BANK_ACCOUNT_REQUESTS,
} from 'api/employeeBankAccount';
import { useMutation } from 'react-query';
import { useQueryClient } from 'react-query';
import { BankAccountVerificationSummary } from './BankAccountVerificationSummary';

export const BulkRejectModal = ({
  isOpen,
  onDismiss,
  selectedRequests,
  onSuccess,
}: {
  isOpen: boolean;
  onDismiss: () => void;
  selectedRequests: Record<string, BankAccountRequest>;
  onSuccess: (failedIds: string[]) => void;
}) => {
  const requestIds = Object.keys(selectedRequests);
  const queryClient = useQueryClient();
  const [comment, setComment] = useState('');
  const isRxCurrentAccountMerchant = !!useSelector(loggedInUserSelectors.currentOrganization)
    ?.isRxCurrentAccountMerchant;
  const toast = useToast();

  const handleDismiss = () => {
    setComment('');
    onDismiss();
  };

  const { mutate: rejectRequests } = useMutation({
    mutationFn: employeeBankAccountApis.bankAccountRequestReject,
    onSuccess: ({ data: { total, successful, failed, failed_request_ids } }) => {
      handleDismiss();
      onSuccess(failed_request_ids);
      queryClient.invalidateQueries({
        queryKey: [GET_BANK_ACCOUNT_REQUESTS],
      });
      if (total && total === successful) {
        toast.show({
          content: 'Requests rejected successfully',
          color: 'positive',
        });
      } else {
        toast.show({
          content: `Failed to reject ${failed} request(s), please try again`,
          color: 'negative',
        });
      }
    },
    onError: () => {
      toast.show({
        content: 'Failed to reject requests, please try later',
        color: 'negative',
      });
    },
  });

  const shouldShowCount = isRxCurrentAccountMerchant;

  const handleSubmit = () => {
    rejectRequests({ reason: comment, change_request_ids: requestIds });
  };

  return (
    <Modal
      zIndex={1003}
      isOpen={isOpen}
      onDismiss={handleDismiss}
      size="small"
      accessibilityLabel="bank account request approval modal">
      <ModalHeader title="Reject Requests" />
      <ModalBody>
        {shouldShowCount && <BankAccountVerificationSummary selectedRequests={selectedRequests} />}
        <Box>
          <Text size="medium" weight="semibold" color="surface.text.gray.normal">
            Are you sure you want to reject these requests?
          </Text>
        </Box>
        <Box marginTop="spacing.6">
          <TextArea
            value={comment}
            onChange={({ value }) => {
              setComment(value ?? '');
            }}
            label="Comment "
            isRequired={isRxCurrentAccountMerchant ? false : true}
            necessityIndicator={isRxCurrentAccountMerchant ? 'optional' : 'required'}
            placeholder="Enter a reason to reject requests"
            marginTop="spacing.3"
          />
        </Box>
      </ModalBody>
      <ModalFooter>
        <Box display="flex" justifyContent="flex-end" gap="spacing.5" width="100%">
          <Button variant="tertiary" onClick={handleDismiss}>
            Cancel
          </Button>
          <Button
            isDisabled={isRxCurrentAccountMerchant ? false : !comment.trim()}
            variant="primary"
            color="negative"
            onClick={handleSubmit}>
            Reject all
          </Button>
        </Box>
      </ModalFooter>
    </Modal>
  );
};